import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'

type SearchParams = { page?: number; search?: string; mint?: string }

export const Route = createFileRoute('/history/')({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || undefined,
    search: (search.search as string) || undefined,
    mint: (search.mint as string) || undefined,
  }),
  component: HistoryPage,
})

const PER_PAGE = 50

function HistoryPage() {
  const { page: qPage, search: qSearch, mint: qMint } = Route.useSearch()
  const navigate = useNavigate({ from: '/history' })

  const page = qPage || 1
  const [searchInput, setSearchInput] = useState(qSearch || '')
  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setSearchInput(qSearch || '')
  }, [qSearch])

  useEffect(() => {
    setLoading(true)
    api.getTransactionsHistory({ page, per_page: PER_PAGE, search: qSearch, mint: qMint })
      .then((res) => {
        setData(res.data)
        setTotal(res.total)
        setTotalPages(res.total_pages)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [page, qSearch, qMint])

  const goTo = (p: Partial<SearchParams>) => {
    navigate({ search: (prev: SearchParams) => ({ ...prev, ...p }) })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    goTo({ search: searchInput.trim() || undefined, page: 1 })
  }

  const clearSearch = () => {
    setSearchInput('')
    goTo({ search: undefined, mint: undefined, page: 1 })
  }

  return (
    <div>
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Transaction History</h1>
      <div style={{ color: 'var(--text-dim)', fontSize: 12, marginBottom: 16 }}>{total.toLocaleString()} transactions</div>

      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          placeholder="Search by signature, mint, or owner..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ flex: 1 }}
        />
        <button type="submit" className="btn btn-primary">Search</button>
        {(qSearch || qMint) && <button type="button" className="btn" onClick={clearSearch}>Clear</button>}
      </form>

      {qMint && (
        <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>
          Filtered by mint: <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{qMint}</span>
          <button type="button" style={{ marginLeft: 8, cursor: 'pointer', color: 'var(--red)', background: 'none', border: 'none', fontSize: 12 }} onClick={() => goTo({ mint: undefined, page: 1 })}>remove</button>
        </div>
      )}

      <div style={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto', opacity: loading ? 0.5 : 1 }}>
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Side</th>
              <th>Mint</th>
              <th>Owner</th>
              <th>SOL</th>
              <th>Volume USD</th>
              <th>Source</th>
              <th>Signature</th>
            </tr>
          </thead>
          <tbody>
            {data.map((tx: any, i: number) => (
              <tr key={tx.signature || i}>
                <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {tx.ts ? new Date(tx.ts).toLocaleString() : '-'}
                </td>
                <td>
                  <span className={`badge ${tx.side === 'buy' ? 'badge-green' : 'badge-red'}`}>{tx.side}</span>
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>
                  <a href={`/tokens/${tx.mint}`}>{tx.mint.slice(0, 12)}...</a>
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{(tx.owner || '').slice(0, 8)}...</td>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{tx.sol_amount?.toFixed(4) || '-'}</td>
                <td style={{ color: 'var(--green)' }}>${tx.volume_usd?.toFixed(2) || '-'}</td>
                <td style={{ fontSize: 12 }}>{tx.source || '-'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{(tx.signature || '').slice(0, 12)}...</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length === 0 && !loading && <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 20 }}>No transactions found</div>}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <button
            className="btn"
            disabled={page <= 1}
            onClick={() => goTo({ page: page - 1 })}
          >
            Prev
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
            Page {page} / {totalPages}
          </span>
          <button
            className="btn"
            disabled={page >= totalPages}
            onClick={() => goTo({ page: page + 1 })}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
